/**
 * Org Chart export contracts — M03 HRIS Wave 8.3.
 *
 * Exports the full reporting hierarchy as flat CSV or nested JSON, derived
 * from the existing `employees.reporting_manager_id` self-FK.
 */
import { z } from 'zod';

import { EmployeeIdSchema } from './employee.js';

export const OrgChartExportFormatSchema = z.enum(['csv', 'json']);
export type OrgChartExportFormat = z.infer<typeof OrgChartExportFormatSchema>;

export const OrgChartExportQuerySchema = z.object({
  format: OrgChartExportFormatSchema.default('json'),
});
export type OrgChartExportQuery = z.infer<typeof OrgChartExportQuerySchema>;

/** Flat row used both for CSV output and as the leaves of the JSON tree. */
export const OrgChartFlatRowSchema = z.object({
  employeeId: EmployeeIdSchema,
  employeeCode: z.string(),
  displayName: z.string(),
  jobTitle: z.string().nullable(),
  managerId: EmployeeIdSchema.nullable(),
  managerCode: z.string().nullable(),
  managerName: z.string().nullable(),
  depth: z.number().int().nonnegative(),
});
export type OrgChartFlatRow = z.infer<typeof OrgChartFlatRowSchema>;

/** Recursive nested-tree node — `children` is a list of the same shape. */
export interface OrgChartNode {
  employeeId: string;
  employeeCode: string;
  displayName: string;
  jobTitle: string | null;
  children: OrgChartNode[];
}

export const OrgChartNodeSchema: z.ZodType<OrgChartNode> = z.lazy(() =>
  z.object({
    employeeId: z.string().uuid(),
    employeeCode: z.string(),
    displayName: z.string(),
    jobTitle: z.string().nullable(),
    children: z.array(OrgChartNodeSchema),
  }),
);

export const OrgChartJsonResponseSchema = z.object({
  generatedAt: z.string().datetime({ offset: true }),
  totalEmployees: z.number().int().nonnegative(),
  roots: z.array(OrgChartNodeSchema),
});
export type OrgChartJsonResponse = z.infer<typeof OrgChartJsonResponseSchema>;
